import { SquashGameType, SquashGameFormat } from './../../_shared/models/enums/squash.enum';
import { FormGroup, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Component, Input, EventEmitter, Output, inject, OnInit, OnDestroy } from '@angular/core';
import { ScoreFormat, SetType } from '../../_shared/models/score-format.model';
import { BoardHelpService } from '../../match-tracker/tracker-board/board-help.service';
import { DisciplineEnum } from '../../_shared/models/csTypes/enums/discipline';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-squash-match-format-form',
  templateUrl: './squash-match-format-form.component.html',
  styleUrls: ['./squash-match-format-form.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    CommonModule,
    ReactiveFormsModule,
    TranslateModule
  ]
})

export class SquashMatchFormatFormComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly bhs = inject(BoardHelpService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$: Subject<void> = new Subject();

  @Input() matchFormat = 'SET3-S:TB11';
  @Input() dividerTitle = this.translateService.instant('Score format');
  @Output() updatedMatchFormat = new EventEmitter();

  scoreFormat!: ScoreFormat;
  form!: FormGroup;

  ngOnInit() {
    this.initScoreFormat();
    this.buildForm();
  }

  private initScoreFormat(): void {
    this.scoreFormat = this.bhs.getScoreFormat(
      this.matchFormat,
      DisciplineEnum.SQUASH
    );
  }

  buildForm() {
    // Determine game type from match format or default to RALLY
    const gameType = this.matchFormat.includes('ENGLISH') || this.matchFormat.includes('HIHO') 
      ? SquashGameType.ENGLISH_HIHO 
      : SquashGameType.RALLY;

       // parsed score format from BoardHelpService
    this.scoreFormat = this.scoreFormat || this.bhs.getScoreFormat(this.matchFormat, DisciplineEnum.SQUASH);
    const parsedPoints = this.scoreFormat?.numberOfPointsInGame;

    // Validate parsed value and pick sensible defaults
    const rallyAllowed = [11, 15, 21];
    const englishAllowed = [9, 15];

    let initialNumberOfPoints: number;
    if (gameType === SquashGameType.ENGLISH_HIHO) {
      initialNumberOfPoints = englishAllowed.includes(parsedPoints) ? parsedPoints! : 9;
    } else {
      initialNumberOfPoints = rallyAllowed.includes(parsedPoints) ? parsedPoints! : 11;
    }
    
    // build form using validated initialNumberOfPoints
    this.form = this.fb.group({
      gameType: [gameType],
      numberOfGames: [this.scoreFormat?.numberOfSets || 3],
      numberOfPoints: [initialNumberOfPoints],
      gameFormat: [SquashGameFormat.WIN_BY_2],
    });

    this.setupSubscriptions();
    this.emitMatchFormat(); // Initial emit
  
    this.form
      .valueChanges
      .subscribe(
        () => {
          this.updatedMatchFormat.emit(this.getMatchFormat());
        }
      );
  }

  private setupSubscriptions(): void {
    // When game type changes, update available points options
    this.form.get('gameType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(gameType => {
        const currentPoints = this.form.get('numberOfPoints')?.value;
        // Reset points if current value is not valid for new game type
        if (gameType === SquashGameType.ENGLISH_HIHO) {
          // English: 9 or 15
          if (currentPoints !== 9 && currentPoints !== 15) {
            this.form.patchValue({ numberOfPoints: 9 }, { emitEvent: false });
          }
        } else {
          // Rally: 11, 15, or 21
          if (currentPoints !== 11 && currentPoints !== 15 && currentPoints !== 21) {
            this.form.patchValue({ numberOfPoints: 11 }, { emitEvent: false });
          }
        }
      });

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emitMatchFormat());
  }

  private emitMatchFormat(): void {
    this.updatedMatchFormat.emit(this.getMatchFormat());
  }

  // Format: SET3-S:TB11 for Rally, SET3-S:TB9:ENGLISH for English (HiHo)
  private getMatchFormat() {
    const { numberOfGames, numberOfPoints, gameType } = this.form.getRawValue();
    const base = `SET${numberOfGames}-S:TB${numberOfPoints}`;
    return gameType === SquashGameType.ENGLISH_HIHO ? `${base}:ENGLISH` : base;
  }

  // Header for action sheet
  customOptions(header: string): { header: string } {
    return { header };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

